import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Printer, AlertTriangle, Clock, Package, Loader2, Check, FileDown } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { pdf, Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";
import { getSettings } from "../lib/printer";
import { showPdfPreview } from "../components/PdfPreview";
import SuggestIngredientsFromInco from "../components/SuggestIngredientsFromInco";
import IngredientCombobox from "../components/IngredientCombobox";
import { COMPANY_HEADER } from "../lib/company";

const emptyRecipe = {
  yield_quantity: "",
  yield_unit: "portions",
  preparation_time_min: "",
  cooking_time_min: "",
  equipment: [],
  ingredients: [],
  steps: [],
  haccp_notes: "",
  conservation_note: "",
};

const sanitize = (r) => {
  const toNum = (v) => v === "" || v === null || v === undefined ? null : (isNaN(Number(v)) ? null : Number(v));
  return {
    yield_quantity: toNum(r.yield_quantity),
    yield_unit: r.yield_unit || "portions",
    preparation_time_min: toNum(r.preparation_time_min),
    cooking_time_min: toNum(r.cooking_time_min),
    equipment: (r.equipment || []).filter(Boolean),
    // Ne jamais filtrer les lignes même si le nom est temporairement vide :
    // l'utilisateur peut être en train de corriger un libellé. Les lignes
    // sont supprimées uniquement via le bouton corbeille.
    ingredients: (r.ingredients || []).map((i) => ({
      // Lien vers la base d'ingrédients centrale — CRITIQUE pour la résolution dynamique
      // des prix (sans cela, chaque sauvegarde casserait le lien et figerait le prix).
      ingredient_id: i.ingredient_id || null,
      name: i.name || "",
      quantity: toNum(i.quantity),
      unit: i.unit || "g",
      note: i.note || "",
      unit_price_chf: toNum(i.unit_price_chf),
      price_unit: i.price_unit || "kg",
      supplier_id: i.supplier_id || null,
      yield_percent: toNum(i.yield_percent),
    })),
    steps: (r.steps || []).map((s) => ({
      description: s.description || "",
      duration_min: toNum(s.duration_min),
      temperature_c: toNum(s.temperature_c),
      haccp_critical: !!s.haccp_critical,
      haccp_note: s.haccp_note || "",
    })),
    haccp_notes: r.haccp_notes || "",
    conservation_note: r.conservation_note || "",
  };
};

const sheetStyles = StyleSheet.create({
  page: { padding: 32, fontFamily: "Helvetica", fontSize: 10, color: "#1A1C1A" },
  header: { borderBottom: "2pt solid #1A1C1A", paddingBottom: 10, marginBottom: 14 },
  brand: { fontSize: 8, color: "#575A57", letterSpacing: 1.5, textTransform: "uppercase" },
  title: { fontSize: 18, fontWeight: 700, marginTop: 2 },
  meta: { fontSize: 9, color: "#575A57", marginTop: 2 },
  section: { marginTop: 14 },
  sectionTitle: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#1F4E3D", borderBottom: "0.5pt solid #E5E5E0", paddingBottom: 3, marginBottom: 6 },
  row: { flexDirection: "row", marginBottom: 3 },
  col: { flex: 1 },
  colLabel: { fontSize: 8, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.5 },
  colValue: { fontSize: 10, fontWeight: 700 },
  ingredientRow: { flexDirection: "row", borderBottom: "0.3pt solid #F0F0EB", paddingVertical: 3 },
  ingName: { flex: 3, fontSize: 9 },
  ingQty: { flex: 1, fontSize: 9, textAlign: "right", fontFamily: "Helvetica-Bold" },
  ingUnit: { width: 30, fontSize: 9, color: "#575A57" },
  ingNote: { flex: 2, fontSize: 8, color: "#575A57", fontFamily: "Helvetica-Oblique" },
  stepBox: { marginBottom: 8, padding: 8, backgroundColor: "#F9F9F6", borderRadius: 2 },
  stepCritical: { backgroundColor: "#FCEEEA", border: "0.5pt solid #C05C47" },
  stepNum: { fontSize: 12, fontWeight: 700, color: "#1F4E3D" },
  stepDesc: { fontSize: 10, marginTop: 2, lineHeight: 1.4 },
  stepMeta: { flexDirection: "row", gap: 14, marginTop: 4, fontSize: 8, color: "#575A57" },
  haccpBadge: { marginLeft: 8, padding: "1 4", backgroundColor: "#C05C47", color: "#fff", fontSize: 7, borderRadius: 2, textTransform: "uppercase" },
  haccpNote: { marginTop: 4, padding: 5, backgroundColor: "#fff", borderLeft: "2pt solid #C05C47", fontSize: 8, color: "#1A1C1A", lineHeight: 1.3 },
  equipment: { padding: "2 6", backgroundColor: "#F0F0EB", borderRadius: 2, fontSize: 8, marginRight: 4, marginBottom: 4 },
  equipRow: { flexDirection: "row", flexWrap: "wrap" },
  notesBox: { padding: 8, backgroundColor: "#F9F9F6", borderRadius: 2, fontSize: 9, lineHeight: 1.4 },
  allergenBox: { padding: 6, backgroundColor: "#FBF4E9", border: "0.5pt solid #E8D3A8", borderRadius: 2, fontSize: 9, marginTop: 4 },
  declaredIngBox: { padding: 8, backgroundColor: "#F9F9F6", border: "0.4pt solid #E5E5E0", borderRadius: 2, fontSize: 9, lineHeight: 1.4, color: "#1A1C1A" },
  nutriHeader: { flexDirection: "row", backgroundColor: "#1F4E3D", padding: "3 5" },
  nutriHeaderLabel: { flex: 3, fontSize: 8, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: 0.5 },
  nutriHeaderValue: { flex: 1, fontSize: 8, fontWeight: 700, color: "#fff", textAlign: "right" },
  nutriRow: { flexDirection: "row", padding: "3 5", borderBottom: "0.3pt solid #F0F0EB" },
  nutriRowAlt: { backgroundColor: "#F9F9F6" },
  nutriLabel: { flex: 3, fontSize: 9, color: "#1A1C1A" },
  nutriLabelIndent: { flex: 3, fontSize: 9, color: "#575A57", paddingLeft: 10, fontFamily: "Helvetica-Oblique" },
  nutriValue: { flex: 1, fontSize: 9, textAlign: "right", fontFamily: "Helvetica-Bold", color: "#1A1C1A" },
  costGrid: { flexDirection: "row", gap: 6 },
  costCell: { flex: 1, padding: 8, backgroundColor: "#F9F9F6", border: "0.4pt solid #E5E5E0", borderRadius: 2 },
  costCellLabel: { fontSize: 7, color: "#575A57", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 },
  costCellValue: { fontSize: 14, fontFamily: "Helvetica-Bold", color: "#1A1C1A" },
  costCellHint: { fontSize: 7, color: "#A3A6A3", marginTop: 1 },
  costMargin: { color: "#1F4E3D", fontFamily: "Helvetica-Bold" },
  footer: { position: "absolute", bottom: 20, left: 32, right: 32, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.3pt solid #E5E5E0", paddingTop: 4 },
});

function TechSheetPdf({ product, recipe, company, generatedAt, totalTime, costBreakdown }) {
  const s = sheetStyles;
  return (
    <Document>
      <Page size="A4" style={s.page}>
        <View style={s.header}>
          <Text style={s.brand}>{company} · Fiche technique HACCP</Text>
          <Text style={s.title}>{product.name}</Text>
          <Text style={s.meta}>
            SKU : {product.sku} · Catégorie : {product.category}
            {product.unit ? ` · Unité : ${product.unit}` : ""}
          </Text>
        </View>

        <View style={s.section}>
          <Text style={s.sectionTitle}>Vue d'ensemble</Text>
          <View style={s.row}>
            <View style={s.col}>
              <Text style={s.colLabel}>Rendement</Text>
              <Text style={s.colValue}>
                {recipe.yield_quantity != null && recipe.yield_quantity !== "" ? `${recipe.yield_quantity} ${recipe.yield_unit || ""}` : "—"}
              </Text>
            </View>
            <View style={s.col}>
              <Text style={s.colLabel}>Préparation</Text>
              <Text style={s.colValue}>{recipe.preparation_time_min ? `${recipe.preparation_time_min} min` : "—"}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.colLabel}>Cuisson</Text>
              <Text style={s.colValue}>{recipe.cooking_time_min ? `${recipe.cooking_time_min} min` : "—"}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.colLabel}>Temps total</Text>
              <Text style={s.colValue}>{totalTime > 0 ? `${totalTime} min` : "—"}</Text>
            </View>
          </View>
        </View>

        {product.allergens?.length > 0 && (
          <View style={s.allergenBox}>
            <Text style={{ fontWeight: 700, color: "#8C6B2F" }}>Allergènes : {product.allergens.join(", ")}</Text>
          </View>
        )}

        {product.ingredients && product.ingredients.trim() !== "" && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Ingrédients (déclaratif emballage)</Text>
            <View style={s.declaredIngBox}>
              <Text>{product.ingredients}</Text>
            </View>
          </View>
        )}

        {product.nutrition && Object.values(product.nutrition).some((v) => v != null && v !== "") && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Valeurs nutritionnelles pour 100 g / 100 mL</Text>
            <View style={{ border: "0.4pt solid #E5E5E0", borderRadius: 2 }}>
              <View style={s.nutriHeader}>
                <Text style={s.nutriHeaderLabel}>Composant</Text>
                <Text style={s.nutriHeaderValue}>Pour 100 g</Text>
              </View>
              {(() => {
                const n = product.nutrition || {};
                const rows = [
                  { label: "Énergie", value: n.energy_kj != null && n.energy_kj !== "" ? `${n.energy_kj} kJ` : null, value2: n.energy_kcal != null && n.energy_kcal !== "" ? ` / ${n.energy_kcal} kcal` : "" },
                  { label: "Matières grasses", value: n.fat != null && n.fat !== "" ? `${n.fat} g` : null },
                  { label: "dont acides gras saturés", value: n.saturated_fat != null && n.saturated_fat !== "" ? `${n.saturated_fat} g` : null, indent: true },
                  { label: "Glucides", value: n.carbs != null && n.carbs !== "" ? `${n.carbs} g` : null },
                  { label: "dont sucres", value: n.sugars != null && n.sugars !== "" ? `${n.sugars} g` : null, indent: true },
                  { label: "Protéines", value: n.proteins != null && n.proteins !== "" ? `${n.proteins} g` : null },
                  { label: "Sel", value: n.salt != null && n.salt !== "" ? `${n.salt} g` : null },
                ].filter((r) => r.value);
                return rows.map((r, i) => (
                  <View key={i} style={[s.nutriRow, i % 2 === 1 ? s.nutriRowAlt : {}]}>
                    <Text style={r.indent ? s.nutriLabelIndent : s.nutriLabel}>{r.label}</Text>
                    <Text style={s.nutriValue}>{r.value}{r.value2 || ""}</Text>
                  </View>
                ));
              })()}
            </View>
          </View>
        )}

        {recipe.equipment?.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Équipements requis</Text>
            <View style={s.equipRow}>
              {recipe.equipment.map((e, i) => (
                <Text key={i} style={s.equipment}>{e}</Text>
              ))}
            </View>
          </View>
        )}

        {recipe.ingredients?.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Ingrédients (recette interne)</Text>
            {recipe.ingredients.map((it, i) => (
              <View key={i} style={s.ingredientRow}>
                <Text style={s.ingName}>{it.name || "—"}</Text>
                <Text style={s.ingQty}>{it.quantity != null && it.quantity !== "" ? it.quantity : "—"}</Text>
                <Text style={s.ingUnit}>{it.unit || ""}</Text>
                <Text style={s.ingNote}>{it.note || ""}</Text>
              </View>
            ))}
          </View>
        )}

        {recipe.steps?.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Étapes de fabrication</Text>
            {recipe.steps.map((st, i) => (
              <View key={i} style={[s.stepBox, st.haccp_critical ? s.stepCritical : {}]} wrap={false}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Text style={s.stepNum}>Étape {i + 1}</Text>
                  {st.haccp_critical && <Text style={s.haccpBadge}>CCP</Text>}
                </View>
                <Text style={s.stepDesc}>{st.description || "—"}</Text>
                <View style={s.stepMeta}>
                  {st.duration_min ? <Text>Durée : {st.duration_min} min</Text> : null}
                  {st.temperature_c != null && st.temperature_c !== "" ? <Text>Température : {st.temperature_c} °C</Text> : null}
                </View>
                {st.haccp_critical && st.haccp_note ? (
                  <Text style={s.haccpNote}>HACCP : {st.haccp_note}</Text>
                ) : null}
              </View>
            ))}
          </View>
        )}

        {(recipe.haccp_notes || recipe.conservation_note) && (
          <View style={s.section}>
            {recipe.haccp_notes ? (
              <>
                <Text style={s.sectionTitle}>Notes HACCP générales</Text>
                <View style={s.notesBox}><Text>{recipe.haccp_notes}</Text></View>
              </>
            ) : null}
            {recipe.conservation_note ? (
              <View style={{ marginTop: recipe.haccp_notes ? 8 : 0 }}>
                <Text style={s.sectionTitle}>Conservation post-fabrication</Text>
                <View style={s.notesBox}><Text>{recipe.conservation_note}</Text></View>
              </View>
            ) : null}
          </View>
        )}

        {(costBreakdown && (costBreakdown.effective_cost != null || product.price_direct || product.price_distributor || product.price_online)) && (
          <View style={s.section} wrap={false}>
            <Text style={s.sectionTitle}>Économie & Marges (CHF, TTC)</Text>
            <View style={s.costGrid}>
              <View style={s.costCell}>
                <Text style={s.costCellLabel}>Coût de revient</Text>
                <Text style={s.costCellValue}>
                  {costBreakdown.effective_cost != null ? `${Number(costBreakdown.effective_cost).toFixed(2)}` : "—"}
                </Text>
                <Text style={s.costCellHint}>
                  {costBreakdown.effective_source === "recipe" ? "Calculé recette" :
                   costBreakdown.effective_source === "recipe_partial" ? `Recette partielle (${Math.round((costBreakdown.completeness || 0) * 100)}%)` :
                   costBreakdown.effective_source === "manual" ? "Saisi manuellement" : ""}
                </Text>
              </View>
              {[
                { key: "price_direct", label: "Vente directe" },
                { key: "price_distributor", label: "Distributeur B2B" },
                { key: "price_online", label: "Internet" },
              ].map((c) => {
                const price = product[c.key];
                const cost = costBreakdown.effective_cost;
                const marginPct = price && cost && price > cost ? Math.round(((price - cost) / price) * 100) : null;
                return (
                  <View key={c.key} style={s.costCell}>
                    <Text style={s.costCellLabel}>{c.label}</Text>
                    <Text style={s.costCellValue}>{price != null ? `${Number(price).toFixed(2)}` : "—"}</Text>
                    <Text style={[s.costCellHint, marginPct != null ? s.costMargin : {}]}>
                      {marginPct != null ? `Marge ${marginPct}%` : "—"}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        <Text style={s.footer} fixed>
          {company} · Fiche technique "{product.name}" · Généré le {generatedAt}
        </Text>
      </Page>
    </Document>
  );
}

export default function TechnicalSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [recipe, setRecipe] = useState(emptyRecipe);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [ingredientsDb, setIngredientsDb] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | dirty | saving | saved
  const [equipmentInput, setEquipmentInput] = useState("");
  const initialLoad = useRef(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setRecipe({
        ...emptyRecipe,
        ...(data.recipe || {}),
        yield_quantity: data.recipe?.yield_quantity ?? "",
        preparation_time_min: data.recipe?.preparation_time_min ?? "",
        cooking_time_min: data.recipe?.cooking_time_min ?? "",
        ingredients: data.recipe?.ingredients || [],
        steps: data.recipe?.steps || [],
        equipment: data.recipe?.equipment || [],
      });
      api.get(`/products/${id}/cost-breakdown`).then((r) => setCostBreakdown(r.data)).catch(() => {});
      api.get(`/suppliers`).then((r) => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
      api.get(`/ingredients`).then((r) => setIngredientsDb(r.data || [])).catch(() => setIngredientsDb([]));
      initialLoad.current = false;
    }).catch(() => {
      toast.error("Produit introuvable");
      navigate("/produits");
    });
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [id, navigate]);

  // Auto-save debouncé (1.2s après le dernier changement)
  useEffect(() => {
    if (initialLoad.current || !product) return;
    setSaveStatus("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await api.put(`/products/${id}`, { recipe: sanitize(recipe) });
        setSaveStatus("saved");
        // Recharger le breakdown coût (les prix d'ingrédients ont peut-être changé)
        api.get(`/products/${id}/cost-breakdown`).then((r) => setCostBreakdown(r.data)).catch(() => {});
      } catch {
        setSaveStatus("dirty");
        toast.error("Échec de l'enregistrement automatique");
      }
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe]);

  const addEquipment = () => {
    const v = equipmentInput.trim();
    if (!v) return;
    setRecipe({ ...recipe, equipment: [...recipe.equipment, v] });
    setEquipmentInput("");
  };
  const removeEquipment = (i) => setRecipe({ ...recipe, equipment: recipe.equipment.filter((_, idx) => idx !== i) });

  const addIngredient = () => setRecipe({ ...recipe, ingredients: [...recipe.ingredients, { name: "", quantity: "", unit: "g", note: "" }] });
  const addIngredientsBulk = (items) => setRecipe((r) => ({ ...r, ingredients: [...r.ingredients, ...items] }));
  const updateIngredient = (i, patch) => setRecipe({ ...recipe, ingredients: recipe.ingredients.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeIngredient = (i) => setRecipe({ ...recipe, ingredients: recipe.ingredients.filter((_, idx) => idx !== i) });

  const addStep = () => setRecipe({ ...recipe, steps: [...recipe.steps, { description: "", duration_min: "", temperature_c: "", haccp_critical: false, haccp_note: "" }] });
  const updateStep = (i, patch) => setRecipe({ ...recipe, steps: recipe.steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) });
  const removeStep = (i) => setRecipe({ ...recipe, steps: recipe.steps.filter((_, idx) => idx !== i) });

  const handlePrint = () => window.print();

  const handleExportPdf = async () => {
    try {
      const settings = getSettings();
      const sanitized = sanitize(recipe);
      const totalTime = (sanitized.preparation_time_min || 0) + (sanitized.cooking_time_min || 0);
      const blob = await pdf(
        <TechSheetPdf
          product={product}
          recipe={sanitized}
          company={COMPANY_HEADER}
          generatedAt={new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
          totalTime={totalTime}
          costBreakdown={costBreakdown}
        />
      ).toBlob();
      const safe = (product.sku || product.name || "fiche").replace(/[^a-zA-Z0-9_-]/g, "-");
      showPdfPreview(blob, `fiche-technique-${safe}.pdf`);
      toast.success("Fiche technique exportée en PDF");
    } catch (e) {
      toast.error("Échec de l'export PDF");
    }
  };

  const SaveIndicator = () => {
    if (saveStatus === "saving") return (
      <span className="text-xs text-[#575A57] flex items-center gap-1.5" data-testid="save-status">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…
      </span>
    );
    if (saveStatus === "saved") return (
      <span className="text-xs text-[#1F4E3D] flex items-center gap-1.5" data-testid="save-status">
        <Check className="w-3.5 h-3.5" /> Enregistré
      </span>
    );
    if (saveStatus === "dirty") return (
      <span className="text-xs text-[#8C6B2F] flex items-center gap-1.5" data-testid="save-status">
        <span className="w-2 h-2 rounded-full bg-[#8C6B2F]"></span> Modifications en cours…
      </span>
    );
    return null;
  };

  if (!product) {
    return (
      <div className="flex items-center justify-center h-64 text-[#575A57]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  const totalTime = (Number(recipe.preparation_time_min) || 0) + (Number(recipe.cooking_time_min) || 0);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="technical-sheet-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <div className="flex items-center gap-3">
          <Link to="/produits">
            <Button variant="ghost" size="icon" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#575A57] font-semibold">Fiche technique</div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1C1A]">{product.name}</h1>
            <div className="text-xs font-mono text-[#575A57]">SKU : {product.sku}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveIndicator />
          <Button variant="outline" onClick={handleExportPdf} data-testid="export-pdf-btn">
            <FileDown className="w-4 h-4 mr-2" /> Export PDF
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="print-sheet-btn">
            <Printer className="w-4 h-4 mr-2" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block border-b-2 border-[#1A1C1A] pb-3 mb-4">
        <div className="font-heading text-2xl font-bold">{product.name}</div>
        <div className="text-sm text-[#575A57]">SKU : {product.sku} · Catégorie : {product.category}</div>
      </div>

      {/* Vue d'ensemble */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium text-[#1A1C1A] flex items-center gap-2">
          <Package className="w-5 h-5 text-[#1F4E3D]" /> Vue d'ensemble
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="mb-1.5 block text-xs">Rendement</Label>
            <div className="flex gap-2">
              <NumericInput value={recipe.yield_quantity} onChange={(e) => setRecipe({ ...recipe, yield_quantity: e.target.value })} placeholder="10" data-testid="yield-quantity" />
              <Input className="w-32" value={recipe.yield_unit} onChange={(e) => setRecipe({ ...recipe, yield_unit: e.target.value })} placeholder="portions" data-testid="yield-unit" />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Préparation (min)</Label>
            <Input type="number" min="0" value={recipe.preparation_time_min} onChange={(e) => setRecipe({ ...recipe, preparation_time_min: e.target.value })} placeholder="20" data-testid="prep-time" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Cuisson (min)</Label>
            <Input type="number" min="0" value={recipe.cooking_time_min} onChange={(e) => setRecipe({ ...recipe, cooking_time_min: e.target.value })} placeholder="35" data-testid="cook-time" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Total</Label>
            <div className="h-10 flex items-center px-3 bg-[#F9F9F6] border border-[#E5E5E0] rounded-md text-sm">
              <Clock className="w-4 h-4 mr-2 text-[#575A57]" />
              {totalTime > 0 ? `${totalTime} min` : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Équipements */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-3">
        <h2 className="font-heading text-lg font-medium text-[#1A1C1A]">Équipements requis</h2>
        <div className="flex flex-wrap gap-2">
          {recipe.equipment.map((e, i) => (
            <Badge key={i} variant="outline" className="bg-[#F0F0EB] border-[#E5E5E0] text-[#1A1C1A] text-xs gap-1.5 pl-3 pr-1.5 py-1">
              {e}
              <button onClick={() => removeEquipment(i)} className="hover:text-[#C05C47] no-print" data-testid={`remove-equipment-${i}`}>×</button>
            </Badge>
          ))}
        </div>
        <div className="flex gap-2 no-print">
          <Input
            value={equipmentInput}
            onChange={(e) => setEquipmentInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEquipment(); } }}
            placeholder="Ex: Sauteuse, Mixeur, Sonde de température…"
            data-testid="equipment-input"
          />
          <Button variant="outline" onClick={addEquipment} data-testid="add-equipment-btn">
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Ingrédients structurés */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading text-lg font-medium text-[#1A1C1A]">Ingrédients (recette interne)</h2>
          <div className="flex items-center gap-3 flex-wrap">
            {costBreakdown && (costBreakdown.unit_cost_from_recipe != null || costBreakdown.total_recipe_cost != null) && (
              <div className="text-xs text-[#575A57] bg-[#F9F9F6] border border-[#E5E5E0] rounded-md px-3 py-1.5 font-mono" data-testid="recipe-cost-summary">
                {costBreakdown.unit_cost_from_recipe != null && costBreakdown.completeness >= 0.99 ? (
                  <>Coût recette : <strong className="text-[#1F4E3D]">{Number(costBreakdown.unit_cost_from_recipe).toFixed(2)} CHF / {costBreakdown.yield_unit || "portion"}</strong></>
                ) : costBreakdown.unit_cost_from_recipe != null ? (
                  <>Estimation partielle ({Math.round((costBreakdown.completeness || 0) * 100)} %) : <strong className="text-[#8C6B2F]">~{Number(costBreakdown.unit_cost_from_recipe).toFixed(2)} CHF</strong></>
                ) : (
                  <>
                    Coût total ingrédients : <strong className="text-[#8C6B2F]">{Number(costBreakdown.total_recipe_cost).toFixed(2)} CHF</strong>
                    <span className="ml-2 text-[10px] text-[#A3A6A3]" title="Le rendement n'est pas renseigné">⚠ saisissez le rendement pour le coût/portion</span>
                  </>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={addIngredient} className="no-print" data-testid="add-ingredient-btn">
              <Plus className="w-4 h-4 mr-1" /> Ingrédient
            </Button>
            <SuggestIngredientsFromInco
              inco={product?.ingredients || ""}
              existing={recipe.ingredients}
              onAdd={addIngredientsBulk}
              ingredientsDb={ingredientsDb}
              onCreateIngredient={(newIng) => {
                setIngredientsDb((prev) => [...prev, newIng].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr")));
              }}
            />
          </div>
        </div>
        {recipe.ingredients.length === 0 ? (
          <p className="text-sm text-[#A3A6A3]">Aucun ingrédient renseigné.</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-wider text-[#575A57] font-semibold px-1 no-print">
              <div className="col-span-3">Nom</div>
              <div className="col-span-2">Quantité utilisée</div>
              <div className="col-span-1">Unité</div>
              <div className="col-span-3">Prix d'achat</div>
              <div className="col-span-2">Note · Fournisseur</div>
              <div className="col-span-1"></div>
            </div>
            {recipe.ingredients.map((it, i) => {
              // Si l'ingrédient est lié à la base, on récupère ses valeurs vivantes
              // (le backend fait pareil dans /cost-breakdown — on doit refléter cela en UI
              // pour que l'utilisateur voie immédiatement l'effet d'un changement de prix
              // dans /parametres/ingredients sans avoir à modifier la recette).
              const linkedIng = it.ingredient_id ? ingredientsDb.find((x) => x.id === it.ingredient_id) : null;
              const resolvedPrice = linkedIng?.default_price_chf ?? it.unit_price_chf;
              const resolvedPriceUnit = linkedIng?.default_price_unit ?? (it.price_unit || "kg");
              const resolvedSupplierId = linkedIng?.default_supplier_id ?? it.supplier_id;
              const resolvedYield = it.yield_percent ?? linkedIng?.yield_percent;
              const priceUnit = resolvedPriceUnit;
              // Même calcul que le backend : conversion vers kg si possible
              const factors = { g: 0.001, kg: 1, mg: 0.000001, ml: 0.001, l: 1, cl: 0.01, dl: 0.1, "100g": 0.1, "100ml": 0.1, pce: null };
              const recipeFactor = factors[(it.unit || "g").toLowerCase()];
              const priceFactor = factors[priceUnit.toLowerCase()];
              let lineCost = null;
              if (Number(resolvedPrice) > 0 && Number(it.quantity) > 0) {
                if (recipeFactor && priceFactor) {
                  const qtyKg = Number(it.quantity) * recipeFactor;
                  const pricePerKg = Number(resolvedPrice) / priceFactor;
                  lineCost = qtyKg * pricePerKg;
                } else {
                  lineCost = Number(it.quantity) * Number(resolvedPrice);
                }
                // Application du rendement comme le backend
                if (resolvedYield && Number(resolvedYield) > 0) {
                  lineCost = lineCost * (100.0 / Number(resolvedYield));
                }
              }
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`ingredient-row-${i}`}>
                  <div className="col-span-3">
                    <IngredientCombobox
                      value={it.name}
                      ingredients={ingredientsDb}
                      onChange={(name, ingredientObj) => {
                        if (ingredientObj) {
                          // Lien strict : on enregistre ingredient_id pour qu'à chaque calcul,
                          // le backend résolve dynamiquement le prix (et propage automatiquement
                          // les changements de prix de la fiche ingrédient vers tous les plats).
                          const patch = {
                            name,
                            ingredient_id: ingredientObj.id,
                          };
                          if (!it.unit && ingredientObj.unit) patch.unit = ingredientObj.unit;
                          if (ingredientObj.default_price_chf != null) {
                            patch.unit_price_chf = ingredientObj.default_price_chf;
                            patch.price_unit = ingredientObj.default_price_unit || "kg";
                          }
                          if (ingredientObj.default_supplier_id) patch.supplier_id = ingredientObj.default_supplier_id;
                          if (ingredientObj.yield_percent != null && it.yield_percent == null) {
                            patch.yield_percent = ingredientObj.yield_percent;
                          }
                          updateIngredient(i, patch);
                        } else {
                          // Texte libre — on délie l'ingrédient
                          updateIngredient(i, { name, ingredient_id: null });
                        }
                      }}
                      onCreate={(newIng) => {
                        // Recharge la liste locale pour refléter le nouveau
                        setIngredientsDb((prev) => [...prev, newIng].sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr")));
                      }}
                      placeholder="Nom de l'ingrédient"
                      testid={`ingredient-name-${i}`}
                    />
                    {it.ingredient_id && (
                      <span
                        className="inline-flex items-center gap-1 mt-1 text-[9px] uppercase tracking-wider text-[#1F4E3D] font-semibold"
                        title="Cet ingrédient est lié à la base. Le prix se met à jour automatiquement quand vous le modifiez dans la fiche ingrédient."
                        data-testid={`ingredient-linked-${i}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1F4E3D]" /> Lié à la base
                      </span>
                    )}
                  </div>
                  <NumericInput className="col-span-2" value={it.quantity} onChange={(e) => updateIngredient(i, { quantity: e.target.value })} placeholder="Qté" />
                  <Input className="col-span-1" value={it.unit} onChange={(e) => updateIngredient(i, { unit: e.target.value })} placeholder="g" />
                  <div className="col-span-3 flex items-start gap-1">
                    <div className="relative flex-1">
                      <NumericInput
                        value={linkedIng ? (resolvedPrice ?? "") : (it.unit_price_chf ?? "")}
                        onChange={(e) => updateIngredient(i, { unit_price_chf: e.target.value })}
                        placeholder="—"
                        readOnly={!!linkedIng}
                        className={`pr-10 font-mono text-sm ${linkedIng ? "bg-[#F0F0EB] text-[#575A57] cursor-not-allowed" : ""}`}
                        title={linkedIng ? "Prix synchronisé avec la fiche ingrédient. Pour modifier, allez dans Paramètres → Ingrédients." : ""}
                        data-testid={`ingredient-price-${i}`}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#A3A6A3] font-mono">CHF</span>
                    </div>
                    <Select
                      value={priceUnit}
                      onValueChange={(v) => updateIngredient(i, { price_unit: v })}
                      disabled={!!linkedIng}
                    >
                      <SelectTrigger className={`w-[72px] shrink-0 ${linkedIng ? "bg-[#F0F0EB] text-[#575A57]" : ""}`} data-testid={`ingredient-price-unit-${i}`}>
                        <span className="text-xs font-mono">/{priceUnit}</span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">/ kg</SelectItem>
                        <SelectItem value="100g">/ 100 g</SelectItem>
                        <SelectItem value="g">/ g</SelectItem>
                        <SelectItem value="L">/ L</SelectItem>
                        <SelectItem value="100mL">/ 100 mL</SelectItem>
                        <SelectItem value="mL">/ mL</SelectItem>
                        <SelectItem value="pce">/ pièce</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input value={it.note} onChange={(e) => updateIngredient(i, { note: e.target.value })} placeholder="Note" />
                    <Select
                      value={resolvedSupplierId || "_none"}
                      onValueChange={(v) => updateIngredient(i, { supplier_id: v === "_none" ? null : v })}
                      disabled={!!linkedIng}
                    >
                      <SelectTrigger className={`mt-1 h-7 text-[11px] ${linkedIng ? "bg-[#F0F0EB] text-[#575A57]" : ""}`} data-testid={`ingredient-supplier-${i}`}>
                        <SelectValue placeholder="Fournisseur…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Aucun fournisseur —</SelectItem>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {lineCost != null && (
                      <div className="text-[11px] text-[#1F4E3D] font-mono mt-0.5 font-semibold" data-testid={`ingredient-line-cost-${i}`}>= {lineCost.toFixed(2)} CHF</div>
                    )}
                    {/* Indicateur d'économie possible (autre fournisseur moins cher) */}
                    {linkedIng && (linkedIng.supplier_prices || []).length >= 2 && (() => {
                      const factors2 = { g: 1000, kg: 1, mg: 1000000, ml: 1000, l: 1, cl: 100, dl: 10, "100g": 10, "100ml": 10, pce: null };
                      const normalized = (linkedIng.supplier_prices || [])
                        .map((sp) => {
                          const f = factors2[(sp.price_unit || "").toLowerCase()];
                          if (!f || !Number(sp.price_chf)) return null;
                          return { supplier_id: sp.supplier_id, price_per_kg: Number(sp.price_chf) * f, is_preferred: sp.is_preferred };
                        })
                        .filter(Boolean);
                      if (normalized.length < 2) return null;
                      const preferred = normalized.find((n) => n.is_preferred);
                      const cheapest = [...normalized].sort((a, b) => a.price_per_kg - b.price_per_kg)[0];
                      if (!preferred || preferred.price_per_kg <= cheapest.price_per_kg) return null;
                      // Ratio de différence sur la quantité utilisée
                      const ratio = (preferred.price_per_kg - cheapest.price_per_kg) / preferred.price_per_kg;
                      const savings = lineCost * ratio;
                      if (savings < 0.01) return null;
                      const altName = suppliers.find((s) => s.id === cheapest.supplier_id)?.name || "Autre fournisseur";
                      return (
                        <div
                          className="text-[10px] text-[#8C6B2F] italic mt-0.5 flex items-start gap-1 leading-snug"
                          data-testid={`saving-hint-${i}`}
                          title={`Prix ${preferred.price_per_kg.toFixed(2)} CHF/kg vs ${cheapest.price_per_kg.toFixed(2)} CHF/kg chez ${altName}`}
                        >
                          <span>💡</span>
                          <span>−{savings.toFixed(2)} CHF chez <strong>{altName}</strong></span>
                        </div>
                      );
                    })()}
                  </div>
                  <Button variant="ghost" size="icon" className="col-span-1 no-print" onClick={() => removeIngredient(i)}>
                    <Trash2 className="w-4 h-4 text-[#C05C47]" />
                  </Button>
                </div>
              );
            })}

            {/* Phase 32 — Récap poids : ingrédients vs poids barquette */}
            {(() => {
              const factorsG = { g: 1, kg: 1000, mg: 0.001, ml: 1, l: 1000, cl: 10, dl: 100, "100g": 100, "100ml": 100 };
              let totalG = 0;
              let hasWeighed = false;
              let hasNonWeighable = false;
              for (const it of recipe.ingredients) {
                const q = Number(it.quantity);
                if (!q || q <= 0) continue;
                const f = factorsG[(it.unit || "g").toLowerCase()];
                if (f != null) {
                  totalG += q * f;
                  hasWeighed = true;
                } else {
                  hasNonWeighable = true; // pce, etc.
                }
              }
              if (!hasWeighed && !hasNonWeighable) return null;

              // Poids net annoncé sur la fiche produit (barquette pesée)
              const packG = (() => {
                const w = Number(product?.net_weight);
                if (!w || w <= 0) return null;
                const u = (product?.net_weight_unit || "g").toLowerCase();
                if (u === "g") return w;
                if (u === "kg") return w * 1000;
                if (u === "ml") return w; // approx 1g/ml
                if (u === "l") return w * 1000;
                return null;
              })();

              // Format affichage : kg si > 1000
              const fmt = (g) => g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${Math.round(g)} g`;

              const overweight = packG != null && totalG > packG;
              const ratio = packG != null && packG > 0 ? totalG / packG : null;

              return (
                <div
                  className="mt-3 border-t border-[#F0F0EB] pt-3 flex items-center justify-between flex-wrap gap-2"
                  data-testid="ingredients-weight-summary"
                >
                  <div className="text-xs text-[#575A57]">
                    <span className="uppercase tracking-wider text-[10px] font-semibold">Total ingrédients (brut)</span>{" "}
                    <span className="font-mono text-sm font-semibold text-[#1A1C1A] ml-2">{fmt(totalG)}</span>
                    {hasNonWeighable && (
                      <span className="ml-2 text-[10px] text-[#A3A6A3] italic">(hors ingrédients à la pièce)</span>
                    )}
                  </div>
                  {packG != null ? (
                    <div className="text-xs">
                      <span className="text-[#575A57]">Poids barquette :</span>{" "}
                      <span className="font-mono font-semibold text-[#1A1C1A]">{fmt(packG)}</span>
                      <span
                        className={`ml-3 font-mono font-semibold ${overweight ? "text-[#C05C47]" : "text-[#1F4E3D]"}`}
                        title="La cuisson peut modifier ce ratio (déshydratation, gonflement du riz, etc.). Vous arbitrez à la cuisine."
                      >
                        {overweight ? "↑" : "↓"} {ratio != null ? `${Math.round(ratio * 100)} %` : ""}
                      </span>
                    </div>
                  ) : (
                    <div className="text-[11px] text-[#A3A6A3] italic">
                      Renseignez le poids net sur la fiche produit pour comparer.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Étapes */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium text-[#1A1C1A]">Étapes de fabrication</h2>
          <Button variant="outline" size="sm" onClick={addStep} className="no-print" data-testid="add-step-btn">
            <Plus className="w-4 h-4 mr-1" /> Étape
          </Button>
        </div>
        {recipe.steps.length === 0 ? (
          <p className="text-sm text-[#A3A6A3]">Aucune étape renseignée.</p>
        ) : (
          <div className="space-y-3">
            {recipe.steps.map((st, i) => (
              <div key={i} className={`border rounded-md p-4 space-y-2 ${st.haccp_critical ? "border-[#C05C47] bg-[#FCEEEA]" : "border-[#E5E5E0] bg-[#F9F9F6]"}`} data-testid={`step-row-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="font-mono font-bold text-[#1F4E3D] text-lg leading-none pt-1">{i + 1}.</div>
                  <Textarea rows={2} value={st.description} onChange={(e) => updateStep(i, { description: e.target.value })} placeholder="Décrire l'étape (ex: Couper le poulet en cubes de 2cm…)" className="flex-1 bg-white" />
                  <Button variant="ghost" size="icon" className="no-print" onClick={() => removeStep(i)}>
                    <Trash2 className="w-4 h-4 text-[#C05C47]" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 ml-7">
                  <div>
                    <Label className="mb-1 block text-xs text-[#575A57]">Durée (min)</Label>
                    <Input type="number" min="0" value={st.duration_min} onChange={(e) => updateStep(i, { duration_min: e.target.value })} placeholder="—" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs text-[#575A57]">Température (°C)</Label>
                    <NumericInput value={st.temperature_c} onChange={(e) => updateStep(i, { temperature_c: e.target.value })} placeholder="—" />
                  </div>
                  <div className="col-span-2 flex items-center gap-2 pt-6">
                    <Checkbox id={`haccp-${i}`} checked={st.haccp_critical} onCheckedChange={(v) => updateStep(i, { haccp_critical: !!v })} data-testid={`haccp-critical-${i}`} />
                    <Label htmlFor={`haccp-${i}`} className="cursor-pointer text-xs flex items-center gap-1 text-[#C05C47] font-medium">
                      <AlertTriangle className="w-3.5 h-3.5" /> Point critique HACCP
                    </Label>
                  </div>
                </div>
                {st.haccp_critical && (
                  <div className="ml-7">
                    <Label className="mb-1 block text-xs text-[#C05C47] font-medium">Note HACCP (limite critique, surveillance, action corrective)</Label>
                    <Textarea rows={2} value={st.haccp_note} onChange={(e) => updateStep(i, { haccp_note: e.target.value })} placeholder="Ex: Atteindre 75°C à cœur pendant 15 min minimum" className="bg-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* HACCP global + conservation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-2">
          <Label className="block font-heading text-base">Notes HACCP générales</Label>
          <Textarea rows={4} value={recipe.haccp_notes} onChange={(e) => setRecipe({ ...recipe, haccp_notes: e.target.value })} placeholder="Précautions générales, allergènes croisés, hygiène…" data-testid="haccp-notes" />
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-2">
          <Label className="block font-heading text-base">Conservation post-fabrication</Label>
          <Textarea rows={4} value={recipe.conservation_note} onChange={(e) => setRecipe({ ...recipe, conservation_note: e.target.value })} placeholder="Ex: ≤ 5°C · 8-10 jours, surgélation à -18°C dans les 2h…" data-testid="conservation-note" />
        </div>
      </div>
    </div>
  );
}
